import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { MagacinProductType } from '../types';
import { magacinProductTypesAPI } from '../services/api';
import './Magacin.css';

const unitOptions = [
  { label: 'kg', value: 'kg' },
  { label: 'kom', value: 'kom' },
  { label: 'm', value: 'm' },
  { label: 'l', value: 'l' }
];

const classOptions = [
  { label: 'Klasa A', value: 'A' },
  { label: 'Klasa B', value: 'B' },
  { label: 'Klasa C', value: 'C' }
];

const MagacinProductTypes: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [items, setItems] = useState<MagacinProductType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<MagacinProductType | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    unit: 'kg',
    minStock: 0,
    maxStock: 1000,
    productClass: 'A' as 'A' | 'B' | 'C'
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await magacinProductTypesAPI.getAll();
      setItems(data);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju tipova proizvoda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({
      name: '',
      description: '',
      unit: 'kg',
      minStock: 0,
      maxStock: 1000,
      productClass: 'A'
    });
    setDialogVisible(true);
  };

  const openEdit = (row: MagacinProductType) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      unit: row.unit || 'kg',
      minStock: row.minStock ?? 0,
      maxStock: row.maxStock ?? 1000,
      productClass: row.productClass || 'A'
    });
    setDialogVisible(true);
  };

  const validate = () => {
    const name = form.name.trim();
    if (!name) return 'Naziv tipa je obavezan';
    if (!form.productClass) return 'Klasa je obavezna';
    if (!form.unit) return 'Jedinica je obavezna';
    if (form.maxStock < form.minStock) return 'Maksimalno stanje mora biti veće ili jednako minimalnom';
    return null;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: validationError, life: 3000 });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || '',
        unit: form.unit,
        minStock: form.minStock ?? 0,
        maxStock: form.maxStock ?? 1000,
        productClass: form.productClass
      };

      if (editing) {
        await magacinProductTypesAPI.update(editing._id, payload);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Tip proizvoda je ažuriran', life: 2500 });
      } else {
        await magacinProductTypesAPI.create(payload);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Tip proizvoda je kreiran', life: 2500 });
      }

      setDialogVisible(false);
      await load();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const del = (row: MagacinProductType) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati tip "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await magacinProductTypesAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Tip obrisan', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju',
            life: 5000
          });
        }
      }
    });
  };

  const classBody = (row: MagacinProductType) => (
    <Tag value={`Klasa ${row.productClass}`} severity={row.productClass === 'A' ? 'success' : row.productClass === 'B' ? 'warning' : 'danger'} />
  );

  const actions = (row: MagacinProductType) => (
    <div className="flex gap-2 justify-content-end">
      <Button icon="pi pi-pencil" className="p-button-sm p-button-text" onClick={() => openEdit(row)} />
      <Button icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={() => del(row)} />
    </div>
  );

  const dialogHeader = useMemo(() => (editing ? 'Izmeni tip proizvoda' : 'Dodaj tip proizvoda'), [editing]);

  return (
    <div className="magacin-page p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="magacin-header flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Kategorije proizvoda</h2>
          <div className="text-600 mt-1">Upravljanje kategorijama</div>
        </div>
        <div className="flex gap-2">
          <Button label="Dodaj kategoriju" icon="pi pi-plus" severity="info" onClick={openAdd} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <DataTable value={items} loading={loading} paginator rows={10} emptyMessage="Nema kategorija" className="p-datatable-sm">
          <Column field="name" header="Naziv kategorije" sortable />
          <Column field="description" header="Opis" />
          <Column header="Klasa" body={classBody} sortable sortField="productClass" style={{ width: '140px' }} />
          <Column field="unit" header="Jedinica" sortable style={{ width: '140px' }} />
          <Column field="minStock" header="Min. stanje" sortable style={{ width: '160px' }} />
          <Column field="maxStock" header="Max. stanje" sortable style={{ width: '160px' }} />
          <Column header="Akcije" body={actions} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '640px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        contentStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        draggable={false}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" severity="info" onClick={save} loading={loading} />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Naziv *</label>
            <InputText value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Klasa *</label>
            <Dropdown
              value={form.productClass}
              options={classOptions}
              onChange={(e) => setForm((p) => ({ ...p, productClass: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Jedinica *</label>
            <Dropdown
              value={form.unit}
              options={unitOptions}
              onChange={(e) => setForm((p) => ({ ...p, unit: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Min. stanje</label>
            <InputNumber value={form.minStock} onValueChange={(e) => setForm((p) => ({ ...p, minStock: e.value ?? 0 }))} min={0} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Max. stanje</label>
            <InputNumber value={form.maxStock} onValueChange={(e) => setForm((p) => ({ ...p, maxStock: e.value ?? 0 }))} min={0} />
          </div>
          <div className="field col-12">
            <label>Opis</label>
            <InputTextarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={5} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default MagacinProductTypes;


